import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { Compass, LogOut, Wallet, Download, Send, Loader2, MessageSquare, Activity } from 'lucide-react';
import { generateScStatementPdf } from '../../utils/scStatement';

const api = axios.create({ baseURL: '/api/sc-portal' });
api.interceptors.request.use((cfg) => { const t = localStorage.getItem('sc_portal_token'); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });
api.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 401) { localStorage.removeItem('sc_portal_token'); localStorage.removeItem('sc_portal_name'); window.location.href = '/sc-portal/login'; }
  return Promise.reject(err);
});

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return ''; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
const LEVELS = { support_connection: 'Support Connection', support_coordination: 'Support Coordination', specialist: 'Specialist Support Coordination' };

export default function ScPortalDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('plan'); // plan | messages
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [dl, setDl] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem('sc_portal_token')) { navigate('/sc-portal/login', { replace: true }); return; }
    Promise.all([api.get('/me'), api.get('/overview')])
      .then(([m, o]) => { setMe(m.data); setOv(o.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const loadMessages = useCallback(() => { api.get('/messages').then((r) => setMessages(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (tab === 'messages') loadMessages(); }, [tab, loadMessages]);
  useEffect(() => { if (tab === 'messages') endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, tab]);

  const logout = () => { localStorage.removeItem('sc_portal_token'); localStorage.removeItem('sc_portal_name'); navigate('/sc-portal/login', { replace: true }); };
  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try { await api.post('/messages', { body: msg.trim() }); setMsg(''); loadMessages(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not send'); } finally { setSending(false); }
  };
  const downloadStatement = async () => {
    setDl(true);
    try {
      // The company letterhead comes with the statement (data.provider). The staff branding
      // endpoint needs a staff login, and its 401 sent participants to the staff /login page.
      const r = await api.get('/statement');
      generateScStatementPdf(r.data, null);
    } catch (e) { toast.error('Could not generate the statement'); } finally { setDl(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;
  if (!me) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 text-sm">Could not load your portal.</div>;

  const coordName = `${me.coord_first || ''} ${me.coord_last || ''}`.trim();
  const remainingPct = ov && ov.totalBudget > 0 ? Math.max(0, Math.min(100, (ov.totalRemaining / ov.totalBudget) * 100)) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="h-9 w-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center flex-shrink-0"><Compass size={18} /></span>
            <div className="min-w-0">
              <p className="font-bold text-slate-800 text-sm leading-tight truncate">{me.first_name} {me.last_name}</p>
              <p className="text-[11px] text-slate-400 truncate">{LEVELS[me.level] || 'Support Coordination'}</p>
            </div>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-slate-600 flex items-center gap-1 text-sm"><LogOut size={16} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4">
        <div className="flex gap-1 my-4 bg-white rounded-xl border border-slate-100 p-1 w-fit">
          {[['plan', 'My plan', Wallet], ['messages', 'Messages', MessageSquare]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium ${tab === k ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Ic size={15} /> {l}</button>
          ))}
        </div>

        {tab === 'plan' && ov && (
          <div className="space-y-4 pb-10">
            {/* Budget hero */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Funds remaining</p>
                  <p className="text-3xl font-extrabold text-emerald-600 tabular-nums mt-1">{money(ov.totalRemaining)}</p>
                  <p className="text-xs text-slate-400 mt-1">of {money(ov.totalBudget)} · {money(ov.totalCommitted)} committed</p>
                </div>
                <button onClick={downloadStatement} disabled={dl} className="text-indigo-600 hover:text-indigo-700 text-sm font-semibold flex items-center gap-1.5">
                  {dl ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Statement
                </button>
              </div>
              <div className="mt-4 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${remainingPct}%` }} /></div>
              {coordName && <p className="text-xs text-slate-400 mt-3">Your coordinator: <span className="text-slate-600 font-medium">{coordName}</span></p>}
            </div>

            {/* Budgets */}
            {ov.budgets.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-100 font-bold text-slate-800 text-sm flex items-center gap-2"><Wallet size={16} className="text-slate-300" /> Budgets</div>
                <div className="divide-y divide-slate-50">
                  {ov.budgets.map((b) => (
                    <div key={b.id} className="px-5 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0"><p className="text-sm font-medium text-slate-700 truncate">{b.budget_name || b.support_category || 'Budget'}</p><p className="text-[11px] text-slate-400">{money(b.budget_amount)} total</p></div>
                      <p className={`text-sm font-semibold tabular-nums ${b.remaining < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{money(b.remaining)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity */}
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 font-bold text-slate-800 text-sm flex items-center gap-2"><Activity size={16} className="text-slate-300" /> Recent support</div>
              {ov.activity.length === 0 ? <p className="px-5 py-6 text-sm text-slate-400 text-center">No activity recorded yet.</p> : (
                <div className="divide-y divide-slate-50">
                  {ov.activity.map((a, i) => (
                    <div key={i} className="px-5 py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0"><p className="text-sm text-slate-700 truncate">{a.support_item_name || a.summary || 'Support coordination'}</p><p className="text-[11px] text-slate-400">{fmtD(a.note_date)}</p></div>
                      <p className="text-sm text-slate-500 tabular-nums whitespace-nowrap">{money(a.line_total)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'messages' && (
          <div className="pb-4">
            <div className="bg-white rounded-2xl border border-slate-100 flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? <p className="text-center text-sm text-slate-400 mt-8">No messages yet. Say hello to your coordinator.</p>
                  : messages.map((m) => (
                    <div key={m.id} className={`flex ${m.direction === 'incoming' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${m.direction === 'incoming' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'}`}>
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${m.direction === 'incoming' ? 'text-indigo-200' : 'text-slate-400'}`}>{fmtD(m.created_at)}</p>
                      </div>
                    </div>
                  ))}
                <div ref={endRef} />
              </div>
              <div className="border-t border-slate-100 p-3 flex items-end gap-2">
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={1} placeholder="Message your coordinator…" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:border-slate-400 focus:ring-0" onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <button onClick={send} disabled={sending || !msg.trim()} className="h-9 w-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center disabled:opacity-50 flex-shrink-0">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
